import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useEstablishment } from '@/contexts/EstablishmentContext';
import { getAICreditsForPlan } from '@/lib/billingPlans';

interface AICreditsData {
  creditsUsed: number;
  creditsBonus: number;
  creditsLimit: number;
  creditsRemaining: number;
  month: string;
}

export function useAICredits() {
  const { currentEstablishment } = useEstablishment();
  const [data, setData] = useState<AICreditsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM

  const fetchCredits = useCallback(async () => {
    if (!currentEstablishment?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // Get current credits usage for this month
      const { data: creditsData, error } = await supabase
        .from('ai_credits')
        .select('credits_used, credits_bonus')
        .eq('establishment_id', currentEstablishment.id)
        .eq('month', currentMonth)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching AI credits:', error);
      }

      const creditsUsed = creditsData?.credits_used ?? 0;
      const creditsBonus = creditsData?.credits_bonus ?? 0;
      const creditsLimit = getAICreditsForPlan(currentEstablishment.planType || 'trial');
      const creditsRemaining = Math.max(0, creditsLimit + creditsBonus - creditsUsed);

      setData({
        creditsUsed,
        creditsBonus,
        creditsLimit,
        creditsRemaining,
        month: currentMonth,
      });
    } catch (error) {
      console.error('Error in useAICredits:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentEstablishment?.id, currentEstablishment?.planType, currentMonth]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  return {
    ...data,
    isLoading,
    refetch: fetchCredits,
  };
}
